/**
 * Pager-feed parsing — pure, I/O-free.
 *
 * A real inbound CAD/pager feed isn't available yet (see integration.ts for
 * the general InboundEvent model this will eventually plug into); in the
 * meantime, duty officers receive incident pages as plain text messages in a
 * known format and need a fast way to turn one into a structured incident.
 * This module only parses and infers — it never fabricates a value it can't
 * read from the text (a malformed/missing field is left undefined, "unknown",
 * never guessed), matching the app's provenance/unknown-first principles.
 *
 * Observed format (NSW RFS duty-officer pager relay):
 *   "<TAG> - <incident number> - <free-text type> - <call type> - <location> - [<lng>,<lat>]"
 * e.g.
 *   "LGDO - 26-121323 - Bush Fire - FIRECALL - TARAGO RD,HOPE DR,BUNGENDORE,
 *    QUEANBEYAN PALERANG (NSW),2621 - [149.462897,-35.219627]"
 * The trailing coordinate segment is sometimes truncated by the paging
 * gateway — that's read as "no coordinates", not as a parse error.
 */

import type { LngLat } from './geo.js';
import type { IncidentType } from './incident-type.js';

/** The literal separator between pager fields — a single space-hyphen-space. Chosen so it never
 * collides with the hyphen inside an incident number like "26-121323". */
const FIELD_SEPARATOR = ' - ';

const COORD_PATTERN = /\[\s*(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)\s*\]/;

// ── Location shorthand ────────────────────────────────────────────────────────
//
// Pager messages are length-restricted, so locations often use a rural-address
// idiom instead of a street address: "<distance> KM <compass dir> BND FROM
// INT/,<road A>,<road B>,…" — e.g. "1 KM S BND FROM INT/,KINGS HWY,MANAR RD"
// means "1 km south-bound from the intersection of Kings Hwy and Manar Rd".
// This only expands known abbreviations into full words — it never invents a
// location; if the shorthand isn't recognised, the original text passes through
// unchanged.

const DIRECTION_WORDS: Record<string, string> = {
  N: 'North',
  S: 'South',
  E: 'East',
  W: 'West',
  NE: 'Northeast',
  NW: 'Northwest',
  SE: 'Southeast',
  SW: 'Southwest',
};

const DISTANCE_FROM_INTERSECTION_PATTERN =
  /^(\d+(?:\.\d+)?)\s*KM\s+([NSEW]{1,2})\s*BND\s+FROM\s+INT\/?$/i;

/**
 * Expand the "<N> KM <DIR> BND FROM INT/" rural shorthand into a readable
 * sentence when the first comma-separated token matches it and the next two
 * tokens are the intersecting road names. Any other shape (a normal street
 * address, an already-readable location) is returned unchanged.
 */
export function expandLocationShorthand(location: string): string {
  const tokens = location.split(',').map((t) => t.trim());
  const first = tokens[0];
  if (!first) return location;
  const m = DISTANCE_FROM_INTERSECTION_PATTERN.exec(first);
  if (!m || tokens.length < 3) return location;

  const [, distanceKm, dirCode] = m;
  const direction = DIRECTION_WORDS[dirCode!.toUpperCase()] ?? dirCode;
  const roadA = tokens[1];
  const roadB = tokens[2];
  const rest = tokens.slice(3);
  return [`${distanceKm} km ${direction}-bound from the intersection of ${roadA} and ${roadB}`, ...rest].join(', ');
}

export interface ParsedPagerEvent {
  /** The original, unmodified text — kept for audit/traceability. */
  raw: string;
  /** Duty-officer / region tag, e.g. "LGDO" (Lake George Duty Officer). */
  dutyOfficerTag?: string;
  /** External incident number, e.g. "26-121323". */
  incidentNumber?: string;
  /** Free-text incident description as paged, e.g. "Bush Fire". */
  typeText?: string;
  /** Call-type / job classification as paged, e.g. "FIRECALL". */
  callType?: string;
  /** Full location text (address/road list + LGA/state + postcode), verbatim. */
  location?: string;
  /** `location` with recognised rural shorthand (distance/direction/intersection) expanded to full words; equals `location` unchanged when no shorthand is recognised. */
  expandedLocation?: string;
  /** Parsed [lng,lat] — undefined if missing or truncated; never guessed. */
  coordinates?: LngLat;
  /** Best-effort IncidentType inference — defaults to 'Other' when genuinely ambiguous. */
  incidentType: IncidentType;
  /** True when the line matched the full expected 6-field shape (tag/number/type/call/location/coords). */
  wellFormed: boolean;
}

/**
 * Conservative, dedicated incident-type inference for pager free text.
 *
 * Deliberately narrower than `incidentTypeFromString` — that generic helper
 * treats any mention of "vehicle" as an MVA (motor vehicle *accident*), which
 * would wrongly reclassify a "Car fire / VEHICLE FIRE" page as a crash. Here,
 * a fire needs a specific structure/vegetation signal to be classified more
 * precisely than 'Other'; an unqualified "unknown fire" or a non-fire hazard
 * (e.g. "Tree down") is left as 'Other' rather than guessed.
 */
function inferIncidentType(typeText: string, callType: string): IncidentType {
  const t = `${typeText} ${callType}`.toLowerCase();
  if (/structure|building|house/.test(t)) return 'StructureFire';
  if (/(bush|grass|scrub|vegetation)/.test(t) && /(fire|alight)/.test(t)) return 'GrassFire';
  if (/\btree\b/.test(t) && /(fire|alight)/.test(t)) return 'GrassFire';
  if (/hazmat|chemical|gas leak|spill/.test(t)) return 'Hazmat';
  if (/storm|flood/.test(t)) return 'StormFlood';
  if (/collision|crash|rollover|\bmva\b|motor vehicle accident/.test(t)) return 'MVA';
  if (/\balarm\b|\bauto\b/.test(t)) return 'Alarm';
  return 'Other';
}

/**
 * Parse one pager-feed line into a structured event. Never throws — a line
 * that doesn't match the expected shape still returns whatever fields could
 * be read, with `wellFormed: false` and `incidentType: 'Other'`.
 */
export function parsePagerEvent(raw: string): ParsedPagerEvent {
  const trimmed = raw.trim();
  const parts = trimmed.split(FIELD_SEPARATOR).map((p) => p.trim());

  const [dutyOfficerTag, incidentNumber, typeText, callType, location, coordsRaw] = parts;
  const wellFormed = parts.length >= 5;

  let coordinates: LngLat | undefined;
  if (coordsRaw) {
    const m = COORD_PATTERN.exec(coordsRaw);
    if (m) {
      const lng = Number(m[1]);
      const lat = Number(m[2]);
      if (Number.isFinite(lng) && Number.isFinite(lat)) coordinates = { lng, lat };
    }
  }

  const incidentType = typeText ? inferIncidentType(typeText, callType ?? '') : 'Other';

  return {
    raw: trimmed,
    dutyOfficerTag: dutyOfficerTag || undefined,
    incidentNumber: incidentNumber || undefined,
    typeText: typeText || undefined,
    callType: callType || undefined,
    location: location || undefined,
    expandedLocation: location ? expandLocationShorthand(location) : undefined,
    coordinates,
    incidentType,
    wellFormed,
  };
}

/** Parse multiple pager lines (one per line; blank lines skipped). */
export function parsePagerFeed(text: string): ParsedPagerEvent[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map(parsePagerEvent);
}

/** A short, human-readable incident name derived from a parsed event, for pre-filling a form. */
export function suggestedIncidentName(event: ParsedPagerEvent): string {
  const type = event.typeText ?? 'Incident';
  return event.incidentNumber ? `${event.incidentNumber} — ${type}` : type;
}
